import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Plus,
  Trash2,
  GripVertical,
  Type,
  Hash,
  List,
  ListChecks,
  ToggleLeft,
  Link2,
  Layers,
  Loader2,
  FileText,
  Upload,
  CheckCircle2,
  AlertTriangle,
  ImageIcon,
  X,
} from "lucide-react";
import api, { photoUrl } from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";

const COLUMN_TYPES = [
  { value: "text", label: "Text", icon: Type, hint: "Free-form short text" },
  { value: "number", label: "Number", icon: Hash, hint: "Numeric (e.g. lumens, CRI)" },
  { value: "dropdown", label: "Dropdown (one pick)", icon: List, hint: "Pick exactly one value per row" },
  { value: "multiselect", label: "Multi-select (offered)", icon: ListChecks, hint: "List of values a row OFFERS — e.g. CCT [3500K, 4000K, 5000K]" },
  { value: "boolean", label: "Yes / No", icon: ToggleLeft, hint: "True on/off attribute (e.g. DLC listed, Wet Location)" },
  { value: "option", label: "Option (spec sheet)", icon: Layers, hint: "Standard / Optional / Not Available — for catalog accessories like Battery Backup" },
  { value: "url", label: "URL", icon: Link2, hint: "Link to external resource" },
];

function toKey(label) {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 50);
}

function randomId() {
  return Math.random().toString(36).slice(2, 10);
}

export default function ProductTypeSchemaSheet({
  open,
  onOpenChange,
  mode = "create",
  manufacturerId,
  productType,
  onSaved,
}) {
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [columns, setColumns] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [aliasInput, setAliasInput] = useState("");
  const [searchMode, setSearchMode] = useState("inherit");
  const [saving, setSaving] = useState(false);
  // Default spec sheet PDF for this product type (returned to users on search)
  const [specPdf, setSpecPdf] = useState(null); // { id, filename } | null
  const [uploadingPdf, setUploadingPdf] = useState(false);
  // Hero image shown on the streamlined spec-sheet card (Step 3 of search)
  const [photoId, setPhotoId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Drag-and-drop visual state for the photo + PDF dropzones.
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const [pdfDragOver, setPdfDragOver] = useState(false);
  // CSV import — only used in edit mode (after PT exists); preview state.
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvParsing, setCsvParsing] = useState(false);
  const [csvCommitting, setCsvCommitting] = useState(false);
  const [csvFile, setCsvFile] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && productType) {
      setName(productType.name || "");
      setDisplayName(productType.display_name || "");
      setDescription(productType.description || "");
      setAliases(productType.aliases || []);
      setSearchMode(productType.search_mode || "inherit");
      setColumns(
        (productType.columns || []).map((c) => ({
          id: c.id || randomId(),
          key: c.key,
          label: c.label,
          type: c.type,
          options: c.options || [],
          required: !!c.required,
        }))
      );
      setSpecPdf(
        productType.default_spec_sheet_pdf_id
          ? {
              id: productType.default_spec_sheet_pdf_id,
              filename: productType.default_spec_sheet_pdf_filename || "spec.pdf",
            }
          : null
      );
      setPhotoId(productType.photo_id || null);
    } else if (mode === "create") {
      setName("");
      setDisplayName("");
      setDescription("");
      setAliases([]);
      setSearchMode("inherit");
      setColumns([]);
      setSpecPdf(null);
      setPhotoId(null);
    }
    setAliasInput("");
    setCsvPreview(null);
    setCsvFile(null);
  }, [open, mode, productType]);

  const addColumn = () => {
    setColumns((prev) => [
      ...prev,
      {
        id: randomId(),
        key: "",
        label: "",
        type: "text",
        options: [],
        required: false,
      },
    ]);
  };

  const updateColumn = (idx, patch) => {
    setColumns((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const removeColumn = (idx) => {
    setColumns((prev) => prev.filter((_, i) => i !== idx));
  };

  const onPdfUpload = async (file) => {
    if (!file) return;
    if (
      file.type !== "application/pdf" &&
      !(file.name || "").toLowerCase().endsWith(".pdf")
    ) {
      toast.error("Please upload a PDF file.");
      return;
    }
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pdfs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSpecPdf({ id: data.id, filename: data.filename });
      toast.success("Spec sheet attached");
    } catch (e) {
      toast.error(formatApiError(e, "Could not upload spec sheet"));
    } finally {
      setUploadingPdf(false);
    }
  };

  const onPhotoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (PNG, JPG, etc.).");
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoId(data.id);
      toast.success("Product photo attached");
    } catch (e) {
      toast.error(formatApiError(e, "Could not upload photo"));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const onCsvSelected = async (file) => {
    if (!file || mode !== "edit" || !productType) return;
    setCsvFile(file);
    setCsvParsing(true);
    setCsvPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/product-types/${productType.id}/csv-import?dry_run=true`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setCsvPreview(data);
    } catch (e) {
      toast.error(formatApiError(e, "Could not read CSV"));
      setCsvFile(null);
    } finally {
      setCsvParsing(false);
    }
  };

  const onCsvCommit = async () => {
    if (!csvFile || !productType) return;
    setCsvCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const { data } = await api.post(
        `/product-types/${productType.id}/csv-import?dry_run=false`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const added = data.products_created || 0;
      const upd = data.products_updated || 0;
      const parts = [];
      if (added) parts.push(`${added} added`);
      if (upd) parts.push(`${upd} updated`);
      toast.success(`Imported ${parts.join(", ") || "rows"}`);
      setCsvPreview(null);
      setCsvFile(null);
      onSaved?.(productType);
    } catch (e) {
      toast.error(formatApiError(e, "Could not import rows"));
    } finally {
      setCsvCommitting(false);
    }
  };

  const onSave = async () => {
    if (!name.trim()) {
      toast.error("Please enter a product type name");
      return;
    }
    // ensure each column has a label and key
    const cleaned = [];
    for (const c of columns) {
      if (!c.label.trim()) continue;
      const key = c.key?.trim() || toKey(c.label);
      cleaned.push({
        id: c.id,
        key,
        label: c.label.trim(),
        type: c.type,
        options: c.type === "dropdown" ? (c.options || []).filter(Boolean) : null,
        required: !!c.required,
      });
    }
    setSaving(true);
    try {
      const cleanedAliases = Array.from(
        new Set(
          aliases
            .map((a) => (a || "").trim())
            .filter(Boolean)
            .map((a) => a.toLowerCase())
        )
      );
      if (mode === "create") {
        const { data } = await api.post(
          `/manufacturers/${manufacturerId}/product-types`,
          {
            name: name.trim(),
            display_name: displayName.trim(),
            description: description.trim(),
            columns: cleaned,
            aliases: cleanedAliases,
            search_mode: searchMode,
            default_spec_sheet_pdf_id: specPdf?.id || null,
            photo_id: photoId || null,
          }
        );
        toast.success("Product type created");
        onSaved?.(data);
      } else if (mode === "edit" && productType) {
        const { data } = await api.patch(`/product-types/${productType.id}`, {
          name: name.trim(),
          display_name: displayName.trim(),
          description: description.trim(),
          columns: cleaned,
          aliases: cleanedAliases,
          search_mode: searchMode,
          default_spec_sheet_pdf_id: specPdf?.id || null,
          photo_id: photoId || null,
        });
        toast.success("Product type updated");
        onSaved?.(data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl p-0 bg-white border-l flex flex-col"
      >
        <SheetHeader className="p-6 border-b border-border shrink-0">
          <SheetTitle className="font-display tracking-tight">
            {mode === "create" ? "New product type" : "Edit product type"}
          </SheetTitle>
          <SheetDescription>
            Define what data this product type tracks. Columns power the
            spreadsheet for every product of this type.
          </SheetDescription>
        </SheetHeader>

        <div className="p-6 space-y-6 flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">
                Type *
                <span className="ml-2 text-[10px] text-muted-foreground normal-case font-normal">
                  big heading on the spec-sheet card · used by search
                </span>
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Area Light, Flat Panel, Exhaust Fan"
                className="mt-1.5 rounded-sm"
                data-testid="pt-name-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">
                Name
                <span className="ml-2 text-[10px] text-muted-foreground normal-case font-normal">
                  subtitle on the card — the product family / series
                </span>
              </Label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder='e.g. "Element Area Light", "Gen 2 Area Light"'
                className="mt-1.5 rounded-sm"
                data-testid="pt-display-name-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">
                Additional notes
                <span className="ml-2 text-[10px] text-muted-foreground normal-case font-normal">
                  short callout shown under the subtitle
                </span>
              </Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder='e.g. "Most economical" or "Best for parking lots"'
                className="mt-1.5 rounded-sm"
                data-testid="pt-description-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">
                Aliases / Synonyms
                <span className="ml-2 text-[10px] text-muted-foreground normal-case font-normal">
                  what distributors call this — for search
                </span>
              </Label>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 p-2 border border-input rounded-sm bg-white min-h-[42px]">
                {aliases.map((a) => (
                  <span
                    key={a}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium"
                  >
                    {a}
                    <button
                      type="button"
                      onClick={() => setAliases((prev) => prev.filter((x) => x !== a))}
                      className="text-amber-700 hover:text-amber-900"
                      aria-label={`Remove ${a}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === ",") && aliasInput.trim()) {
                      e.preventDefault();
                      const next = aliasInput.trim().toLowerCase();
                      if (next && !aliases.includes(next)) {
                        setAliases((prev) => [...prev, next]);
                      }
                      setAliasInput("");
                    } else if (e.key === "Backspace" && !aliasInput && aliases.length) {
                      setAliases((prev) => prev.slice(0, -1));
                    }
                  }}
                  onBlur={() => {
                    const next = aliasInput.trim().toLowerCase();
                    if (next && !aliases.includes(next)) {
                      setAliases((prev) => [...prev, next]);
                      setAliasInput("");
                    }
                  }}
                  placeholder={aliases.length === 0 ? 'e.g. "parking lot light", "pole light", "2x4 panel"' : "add another…"}
                  className="flex-1 min-w-[120px] text-sm bg-transparent outline-none"
                  data-testid="pt-alias-input"
                />
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">
                Press Enter or comma to add. Stored lowercase. Helps distributors find this product type even when they use a different name.
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">
                Search mode
                <span className="ml-2 text-[10px] text-muted-foreground normal-case font-normal">
                  overrides the manufacturer default
                </span>
              </Label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                {[
                  {
                    value: "inherit",
                    label: "Use manufacturer",
                    hint: "Inherit from the manufacturer's setting",
                  },
                  {
                    value: "streamlined",
                    label: "Streamlined",
                    hint: "Returns this PDF directly — ignores the CSV table",
                  },
                  {
                    value: "guided",
                    label: "AI wizard",
                    hint: "Walks the user through filters (uses CSV table)",
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSearchMode(opt.value)}
                    className={`text-left rounded-sm border px-3 py-2 transition-colors ${
                      searchMode === opt.value
                        ? "border-amber-500 bg-amber-50 text-slate-900"
                        : "border-border bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`pt-search-mode-${opt.value}`}
                  >
                    <div className="text-xs font-semibold">{opt.label}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{opt.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Product photo — hero image on the streamlined spec-sheet card */}
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">
              Product photo
            </Label>
            <p className="text-xs text-muted-foreground mb-3">
              Optional. Shown as the hero image on the spec-sheet card during
              streamlined search. PNG or JPG works best.
            </p>
            {photoId ? (
              <div
                className="flex items-center gap-3 bg-slate-50 border border-border rounded-sm p-2"
                data-testid="pt-photo-attached"
              >
                <img
                  src={photoUrl(photoId)}
                  alt="Product"
                  className="w-16 h-16 object-cover rounded-sm bg-white border border-border"
                />
                <span className="flex-1 text-sm text-slate-700">
                  Photo attached
                </span>
                <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs text-slate-700 hover:text-amber-700 border border-border hover:border-amber-300 rounded-sm px-2 py-1">
                  <Upload className="w-3.5 h-3.5" />
                  Replace
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingPhoto}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onPhotoUpload(f);
                      e.target.value = "";
                    }}
                    data-testid="pt-photo-replace-input"
                  />
                </label>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-sm h-7 px-2"
                  onClick={() => setPhotoId(null)}
                  data-testid="pt-photo-remove-btn"
                  title="Remove"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <label
                onDragOver={(e) => {
                  e.preventDefault();
                  setPhotoDragOver(true);
                }}
                onDragLeave={() => setPhotoDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setPhotoDragOver(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) onPhotoUpload(f);
                }}
                className={`flex items-center gap-2 cursor-pointer border border-dashed rounded-sm px-3 py-6 text-sm transition-colors ${
                  photoDragOver
                    ? "border-amber-500 bg-amber-50 text-amber-900"
                    : "border-border hover:border-amber-300 text-slate-700"
                }`}
                data-testid="pt-photo-dropzone"
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                ) : (
                  <ImageIcon className="w-4 h-4 text-amber-600" />
                )}
                <span className="font-medium">
                  {uploadingPhoto
                    ? "Uploading…"
                    : photoDragOver
                    ? "Drop image to upload"
                    : "Upload product photo — or drag and drop"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingPhoto}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onPhotoUpload(f);
                    e.target.value = "";
                  }}
                  data-testid="pt-photo-upload-input"
                />
              </label>
            )}
          </div>

          {/* Default spec sheet PDF — sent to distributors on search */}
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">
              Default spec sheet PDF
            </Label>
            <p className="text-xs text-muted-foreground mb-3">
              Optional. Uploaded once and shown to distributors as the official
              spec sheet when they find any product in this table.
            </p>
            {specPdf ? (
              <div
                className="flex items-center gap-2 bg-slate-50 border border-border rounded-sm px-3 py-2 text-sm"
                data-testid="spec-pdf-attached"
              >
                <FileText className="w-4 h-4 text-amber-600 shrink-0" />
                <span className="flex-1 truncate font-medium text-slate-900">
                  {specPdf.filename}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-sm h-7 px-2"
                  onClick={() => setSpecPdf(null)}
                  data-testid="spec-pdf-remove-btn"
                  title="Remove"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <label
                onDragOver={(e) => {
                  e.preventDefault();
                  setPdfDragOver(true);
                }}
                onDragLeave={() => setPdfDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setPdfDragOver(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) onPdfUpload(f);
                }}
                className={`flex items-center gap-2 cursor-pointer border border-dashed rounded-sm px-3 py-6 text-sm transition-colors ${
                  pdfDragOver
                    ? "border-amber-500 bg-amber-50 text-amber-900"
                    : "border-border hover:border-amber-300 text-slate-700"
                }`}
                data-testid="spec-pdf-dropzone"
              >
                {uploadingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                ) : (
                  <Upload className="w-4 h-4 text-amber-600" />
                )}
                <span className="font-medium">
                  {uploadingPdf
                    ? "Uploading…"
                    : pdfDragOver
                    ? "Drop PDF to upload"
                    : "Upload spec sheet PDF — or drag and drop"}
                </span>
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  disabled={uploadingPdf}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onPdfUpload(f);
                    e.target.value = "";
                  }}
                  data-testid="spec-pdf-upload-input"
                />
              </label>
            )}
          </div>

          {/* CSV table import — edit mode only (need columns to map against) */}
          {mode === "edit" && productType && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider">
                  Bulk-import rows from CSV
                </Label>
                <div className="flex items-center gap-3">
                  <a
                    href="#"
                    className="text-xs text-amber-700 hover:text-amber-800 underline font-medium"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const res = await api.get(
                          `/product-types/${productType.id}/csv-export`,
                          { responseType: "blob" }
                        );
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement("a");
                        a.href = url;
                        const safe = (productType.name || "export").replace(
                          /[^a-z0-9-_]/gi,
                          "-"
                        );
                        a.download = `${safe}-export.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        toast.error(formatApiError(err, "Could not download data"));
                      }
                    }}
                    data-testid="csv-export-link"
                  >
                    Download current data
                  </a>
                  <span className="text-muted-foreground text-xs">·</span>
                  <a
                    href="#"
                    className="text-xs text-amber-700 hover:text-amber-800 underline font-medium"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const res = await api.get(
                          `/product-types/${productType.id}/csv-template`,
                          { responseType: "blob" }
                        );
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement("a");
                        a.href = url;
                        const safe = (productType.name || "template").replace(
                          /[^a-z0-9-_]/gi,
                          "-"
                        );
                        a.download = `${safe}-template.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        toast.error(formatApiError(err, "Could not download template"));
                      }
                    }}
                    data-testid="csv-template-link"
                  >
                    Empty template
                  </a>
                </div>
              </div>
              <CsvImportFormatHint />
              {!csvPreview && !csvParsing && (
                <label className="inline-flex items-center gap-2 cursor-pointer border border-dashed border-border hover:border-amber-300 rounded-sm px-3 py-2 text-sm text-slate-700 mt-2">
                  <Upload className="w-4 h-4 text-amber-600" />
                  <span className="font-medium">Choose .csv file</span>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onCsvSelected(f);
                      e.target.value = "";
                    }}
                    data-testid="csv-import-input"
                  />
                </label>
              )}
              {csvParsing && (
                <div className="mt-2 inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> Parsing CSV…
                </div>
              )}
              {csvPreview && (
                <CsvPreview
                  preview={csvPreview}
                  file={csvFile}
                  committing={csvCommitting}
                  onCommit={onCsvCommit}
                  onCancel={() => {
                    setCsvPreview(null);
                    setCsvFile(null);
                  }}
                />
              )}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider">
                Spec columns
              </Label>
              <Button
                size="sm"
                variant="outline"
                onClick={addColumn}
                className="rounded-sm"
                data-testid="add-column-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Add column
              </Button>
            </div>

            {columns.length === 0 ? (
              <div className="border border-dashed border-border rounded-sm p-6 text-center text-sm text-muted-foreground">
                No columns yet. Add columns like "Lumens", "Wattage", "Wire Gauge".
              </div>
            ) : (
              <div className="space-y-2">
                {columns.map((c, idx) => (
                  <ColumnRow
                    key={c.id}
                    column={c}
                    onChange={(patch) => updateColumn(idx, patch)}
                    onRemove={() => removeColumn(idx)}
                    index={idx}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <SheetFooter className="p-6 border-t border-border bg-slate-50 shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="pt-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {mode === "create" ? "Create product type" : "Save changes"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function ColumnRow({ column, onChange, onRemove, index }) {
  const typeMeta = COLUMN_TYPES.find((t) => t.value === column.type);
  const TypeIcon = typeMeta?.icon || Type;

  return (
    <div className="border border-border rounded-sm bg-white p-3" data-testid={`column-row-${index}`}>
      <div className="flex items-start gap-2">
        <div className="pt-2 text-muted-foreground">
          <GripVertical className="w-4 h-4" />
        </div>
        <div className="flex-1 grid grid-cols-12 gap-2">
          <div className="col-span-12 sm:col-span-6">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Label
            </Label>
            <Input
              value={column.label}
              onChange={(e) =>
                onChange({
                  label: e.target.value,
                  key: column.key || toKey(e.target.value),
                })
              }
              placeholder="e.g. Lumens"
              className="mt-1 rounded-sm h-9"
            />
          </div>
          <div className="col-span-7 sm:col-span-4">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Type
            </Label>
            <Select
              value={column.type}
              onValueChange={(v) => onChange({ type: v, options: ["dropdown", "multiselect"].includes(v) ? column.options : [] })}
            >
              <SelectTrigger className="mt-1 rounded-sm h-9">
                <span className="inline-flex items-center gap-2">
                  <TypeIcon className="w-3.5 h-3.5" />
                  <SelectValue />
                </span>
              </SelectTrigger>
              <SelectContent>
                {COLUMN_TYPES.map((t) => {
                  const Icon = t.icon;
                  return (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="inline-flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5" /> {t.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-5 sm:col-span-2 flex items-end">
            <label className="flex items-center gap-2 text-xs text-slate-600 h-9 mt-1">
              <Switch
                checked={!!column.required}
                onCheckedChange={(v) => onChange({ required: v })}
              />
              Required
            </label>
          </div>
          {(column.type === "dropdown" || column.type === "multiselect") && (
            <div className="col-span-12">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {column.type === "multiselect" ? "Available options (comma-separated)" : "Options (comma-separated)"}
              </Label>
              <Input
                value={(column.options || []).join(", ")}
                onChange={(e) =>
                  onChange({
                    options: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                placeholder="e.g. 3000K, 4000K, 5000K"
                className="mt-1 rounded-sm h-9"
              />
            </div>
          )}
          {typeMeta?.hint && (
            <div className="col-span-12 text-[11px] text-muted-foreground">
              {typeMeta.hint}
            </div>
          )}
        </div>
        <button
          onClick={onRemove}
          className="p-2 text-muted-foreground hover:text-destructive"
          data-testid={`remove-column-${index}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}


function CsvImportFormatHint() {
  return (
    <details className="text-xs text-muted-foreground border border-border rounded-sm">
      <summary className="cursor-pointer px-3 py-2 hover:bg-slate-50 font-medium text-slate-700">
        Show CSV format guide
      </summary>
      <div className="px-3 py-2 space-y-2 border-t border-border">
        <div>
          <span className="font-mono-data text-[10px] uppercase tracking-widest text-amber-700">
            Header row
          </span>{" "}
          must match your column labels (case-insensitive). Reserved headers:{" "}
          <code className="bg-slate-100 px-1 rounded">name</code>,{" "}
          <code className="bg-slate-100 px-1 rounded">model_number</code>.
        </div>
        <div>
          <span className="font-mono-data text-[10px] uppercase tracking-widest text-amber-700">
            Cell formats
          </span>
          <ul className="mt-1 list-disc pl-4 space-y-0.5">
            <li>
              <strong>multiselect:</strong>{" "}
              <code className="bg-slate-100 px-1 rounded">3000K|4000K|5000K</code>{" "}
              (pipe-separated)
            </li>
            <li>
              <strong>boolean:</strong> yes / no
            </li>
            <li>
              <strong>option:</strong> Standard / Optional / Not Available
            </li>
            <li>
              <strong>number:</strong> plain number, no units
            </li>
          </ul>
        </div>
        <div>
          Rows with a matching <code className="bg-slate-100 px-1 rounded">model_number</code> will
          be <strong>updated</strong>. New model numbers are <strong>inserted</strong>.
        </div>
      </div>
    </details>
  );
}

function CsvPreview({ preview, file, committing, onCommit, onCancel }) {
  const rows = preview.rows || [];
  const unknownHeaders = preview.unknown_headers || [];
  const warningCount = (preview.row_errors || []).reduce(
    (n, r) => n + (r.warnings?.length || 0),
    0
  );
  // Build a column set from all attributes across rows for the preview table
  const attrKeys = Array.from(
    rows.reduce((s, r) => {
      Object.keys(r.attributes || {}).forEach((k) => s.add(k));
      return s;
    }, new Set())
  );

  return (
    <div className="mt-3 border border-border rounded-sm" data-testid="csv-preview">
      <div className="px-3 py-2 bg-slate-50 border-b border-border flex items-center gap-3">
        <FileText className="w-4 h-4 text-amber-600" />
        <span className="font-medium text-sm truncate flex-1">
          {file?.name || "preview"}
        </span>
        <span className="text-xs text-muted-foreground">
          {preview.row_count} row{preview.row_count === 1 ? "" : "s"}
          {warningCount > 0 ? ` · ${warningCount} warnings` : ""}
        </span>
      </div>

      {unknownHeaders.length > 0 && (
        <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <strong>Unknown headers (will be skipped):</strong>{" "}
            {unknownHeaders.join(", ")}.{" "}
            Add these as columns first if you want them imported.
          </div>
        </div>
      )}

      <div className="max-h-72 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white border-b border-border">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">#</th>
              <th className="text-left px-2 py-1.5 font-medium">Name</th>
              <th className="text-left px-2 py-1.5 font-medium">Model #</th>
              {attrKeys.map((k) => (
                <th key={k} className="text-left px-2 py-1.5 font-medium">
                  {k}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 50).map((r) => (
              <tr key={r.line} className="border-b border-border last:border-b-0">
                <td className="px-2 py-1.5 text-muted-foreground font-mono-data">
                  {r.line}
                </td>
                <td className="px-2 py-1.5 font-medium">{r.name}</td>
                <td className="px-2 py-1.5 font-mono-data">{r.model_number || "—"}</td>
                {attrKeys.map((k) => {
                  const v = r.attributes?.[k];
                  return (
                    <td key={k} className="px-2 py-1.5">
                      {Array.isArray(v) ? v.join(", ") : v === true ? "Yes" : v === false ? "No" : v ?? ""}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 50 && (
          <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
            Showing first 50 of {rows.length} rows
          </div>
        )}
      </div>

      <div className="px-3 py-2 bg-slate-50 border-t border-border flex items-center justify-end gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={onCancel}
          disabled={committing}
          className="rounded-sm"
          data-testid="csv-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={onCommit}
          disabled={committing || rows.length === 0}
          className="bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-sm font-semibold"
          data-testid="csv-commit-btn"
        >
          {committing ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
          )}
          Import {rows.length} row{rows.length === 1 ? "" : "s"}
        </Button>
      </div>
    </div>
  );
}
